/**
 * Shopify storefront privacy-banner readability fix.
 *
 * The banner is loaded by Shopify's storefront-banner.js as a custom
 * element with an open shadow DOM, which means the theme's stylesheets
 * can't reach inside. Without this script the banner inherits some
 * theme colors that render white-on-white in our case.
 *
 * Strategy:
 *  1. Set CSS custom properties on the host element (Shopify documents
 *     `--banner-*-color` variables for theme integration — set them
 *     defensively even though their exact names vary across versions).
 *  2. Inject a small stylesheet directly into the shadow root that
 *     forces a readable dark background with white text and an amber
 *     primary button matching our brand.
 *  3. Wait for the banner event-driven (MutationObserver + custom-element
 *     upgrade), and re-apply if Shopify recreates the banner shortly after
 *     load.
 */
(function () {
  if (window.__organaPrivacyFix) return;
  window.__organaPrivacyFix = true;

  var HOST_VARS = [
    '--banner-background-color: #1f2328',
    '--banner-text-color: #ffffff',
    '--banner-button-background-color: #f5a623',
    '--banner-button-text-color: #1f1a10',
    '--banner-button-border-color: #f5a623',
    '--banner-link-color: #f5a623',
    '--banner-link-hover-color: #ffd277'
  ].join(';') + ';';

  var SHADOW_CSS = [
    /* Container surfaces */
    ':host, :host([role]) { color-scheme: dark; }',
    ':host > div, :host section, :host article, :host .container, :host .banner, :host .privacy-banner {',
    '  background: #1f2328 !important;',
    '  color: #ffffff !important;',
    '  border-color: rgba(255, 255, 255, 0.12) !important;',
    '}',
    /* Text */
    ':host *, :host p, :host span, :host h1, :host h2, :host h3, :host h4, :host h5, :host h6, :host label, :host strong, :host em {',
    '  color: #ffffff !important;',
    '}',
    /* Links */
    ':host a { color: #f5a623 !important; text-decoration: underline; }',
    ':host a:hover { color: #ffd277 !important; }',
    /* Primary button — amber */
    ':host button, :host [role="button"] {',
    '  background: #f5a623 !important;',
    '  color: #1f1a10 !important;',
    '  border: 1px solid #f5a623 !important;',
    '}',
    ':host button:hover, :host [role="button"]:hover {',
    '  background: #ffd277 !important;',
    '  border-color: #ffd277 !important;',
    '}',
    /* Secondary / outline / decline-style buttons — ghost on dark */
    ':host button[data-secondary],',
    ':host button.secondary,',
    ':host button.decline,',
    ':host button.outline,',
    ':host button[aria-label*="manage" i],',
    ':host button[aria-label*="decline" i] {',
    '  background: transparent !important;',
    '  color: #ffffff !important;',
    '  border: 1px solid rgba(255, 255, 255, 0.4) !important;',
    '}'
  ].join('\n');

  function findHost() {
    return (
      document.querySelector('shopify-privacy-banner') ||
      document.querySelector('[id*="privacy-banner"]') ||
      document.querySelector('[class*="privacy-banner"]') ||
      null
    );
  }

  function patch(host) {
    if (!host) return false;
    try {
      // Apply CSS variables on the host
      var prev = host.getAttribute('style') || '';
      if (prev.indexOf('--banner-background-color') === -1) {
        host.setAttribute('style', prev + ';' + HOST_VARS);
      }
    } catch (e) {}

    var root = host.shadowRoot;
    if (!root) return false;
    if (root.getElementById('organa-privacy-fix')) return true;

    var style = document.createElement('style');
    style.id = 'organa-privacy-fix';
    style.textContent = SHADOW_CSS;
    root.appendChild(style);
    return true;
  }

  // ---- Waiting mechanism (event-driven, no polling) ----------------------
  //
  // Previously: a 150ms setTimeout polling loop (up to 20s) plus a
  // whole-document subtree MutationObserver kept alive for 30s. Replaced by:
  //  - one immediate attempt at DOM-ready;
  //  - a MutationObserver that disconnects as soon as the banner is patched,
  //    with a hard 10s safety disconnect;
  //  - customElements.whenDefined() to catch the custom-element upgrade
  //    (shadow root attach without any DOM mutation);
  //  - after the first patch, a cheap childList-only observer on the banner's
  //    parent preserves the old "re-patch if Shopify swaps the banner within
  //    30s of load" behavior at near-zero cost.

  var START = Date.now();
  var FIND_TIMEOUT_MS = 10000;
  var SWAP_WINDOW_MS = 30000;

  function tryPatch() {
    var host = findHost();
    return host && patch(host) ? host : null;
  }

  // Legacy recurrence semantics: if Shopify recreates the banner within 30s
  // of load, re-patch it. Watches only direct children of the host's parent.
  function watchForSwap(host) {
    var parent = host && host.parentNode;
    var remaining = SWAP_WINDOW_MS - (Date.now() - START);
    if (!parent || remaining <= 0) return;
    try {
      var swapObs = new MutationObserver(function () {
        var current = tryPatch();
        if (current && current !== host) {
          swapObs.disconnect();
          watchForSwap(current);
        }
      });
      swapObs.observe(parent, { childList: true });
      setTimeout(function () { swapObs.disconnect(); }, remaining);
    } catch (e) {}
  }

  function waitForBanner() {
    var found = tryPatch();
    if (found) {
      watchForSwap(found);
      return;
    }

    var settled = false;
    var obs = null;
    var timer = null;

    function succeed(host) {
      if (settled) return;
      settled = true;
      if (obs) obs.disconnect();
      if (timer) clearTimeout(timer);
      watchForSwap(host);
    }

    function retry() {
      if (settled) return;
      var host = tryPatch();
      if (host) succeed(host);
    }

    try {
      obs = new MutationObserver(retry);
      obs.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
      });
    } catch (e) {}

    // The custom element can be parsed before its definition loads; the
    // upgrade attaches the shadow root without any DOM mutation.
    try {
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('shopify-privacy-banner').then(retry);
      }
    } catch (e) {}

    // Hard safety stop: stop observing after 10s. Deliberately does NOT set
    // `settled`, so the one-shot customElements.whenDefined retry stays live
    // for a banner that defines later than the observer window.
    timer = setTimeout(function () {
      if (obs) obs.disconnect();
    }, FIND_TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForBanner);
  } else {
    waitForBanner();
  }
})();
